import { BotTasks, Segment } from "@apis:bot_tasks/bot_tasks";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { formatTime } from "@pages:app/audio/util";
import { getColorFromId } from "@utils:color";
import { downloadJSON } from "@utils:file";
import { get, isEmpty } from "lodash";
import { Fragment, useState } from "react";

enum ResultTab {
  Transcription = "Transcription",
  Diarization = "Diarization",
}

interface TranscriptionProps {
  transcription: BotTasks["GET"]["Response"]["transcription"];
  diarization: BotTasks["GET"]["Response"]["diarization"];
  sx?: SxProps;
}

const TabSelector = ({
  activeTab,
  onTabChange,
}: {
  activeTab: ResultTab;
  onTabChange: (tab: ResultTab) => void;
}) => (
  <Stack direction="row" spacing={1}>
    <Typography
      variant="body1"
      color="text.primary"
      gutterBottom
      sx={{
        fontWeight: "bold",
        opacity: 0.8,
      }}
    >
      Results
    </Typography>
    <Chip
      label="Transcription"
      size="small"
      onClick={() => onTabChange(ResultTab.Transcription)}
      color={activeTab === ResultTab.Transcription ? "primary" : "default"}
      variant={activeTab === ResultTab.Transcription ? "filled" : "outlined"}
    />
    <Chip
      label="Diarization"
      size="small"
      onClick={() => onTabChange(ResultTab.Diarization)}
      color={activeTab === ResultTab.Diarization ? "primary" : "default"}
      variant={activeTab === ResultTab.Diarization ? "filled" : "outlined"}
    />
  </Stack>
);

const DownloadButton = ({
  tab,
  data,
  disabled,
}: {
  tab: ResultTab;
  data: any;
  disabled: boolean;
}) => (
  <Button
    startIcon={
      <SVGInternalIcon
        icon="download"
        sx={{ color: "blue", fontSize: "small" }}
      />
    }
    disabled={disabled}
    onClick={() => downloadJSON(data, `${tab}.json`)}
  >
    Download {tab}
  </Button>
);

const SegmentsList = ({
  segments,
  tab,
}: {
  segments: Segment[] | undefined;
  tab: ResultTab;
}) => {
  if (!segments || !Array.isArray(segments) || segments.length === 0) {
    return (
      <ListItem>
        <Alert
          severity="info"
          sx={{ width: "100%", backgroundColor: "transparent" }}
        >
          No {tab === ResultTab.Diarization ? "diarization" : "transcription"}{" "}
          data available.
        </Alert>
      </ListItem>
    );
  }

  return (
    <>
      {segments.map((segment, index) => {
        const speakerColor = getColorFromId(segment.speaker || "");
        const languageColor = getColorFromId(segment.language || "");

        return (
          <Fragment key={index}>
            {index > 0 && <Divider component="li" />}
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <>
                    {segment.speaker && (
                      <Chip
                        label={`Speaker ${segment.speaker}`}
                        size="small"
                        sx={{
                          height: "18px",
                          marginRight: 1,
                          backgroundColor: speakerColor,
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                    {segment.language && (
                      <Chip
                        label={`${segment.language}`}
                        size="small"
                        sx={{
                          height: "18px",
                          marginRight: 1,
                          backgroundColor: languageColor,
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                    {segment.text}
                  </>
                }
                secondary={`${formatTime(segment.start)} - ${formatTime(segment.end)}`}
              />
            </ListItem>
          </Fragment>
        );
      })}
    </>
  );
};

export const Results = ({
  transcription,
  diarization,
  sx,
}: TranscriptionProps) => {
  const [tab, setTab] = useState<ResultTab>(ResultTab.Transcription);

  const currentData =
    tab === ResultTab.Transcription ? transcription : diarization;
  const isDataEmpty = isEmpty(currentData);

  return (
    <Box sx={sx}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginBottom: "10px" }}
      >
        <TabSelector activeTab={tab} onTabChange={setTab} />
        <DownloadButton tab={tab} data={currentData} disabled={isDataEmpty} />
      </Stack>

      <List
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.03)",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          "& .MuiListItem-root": {
            paddingY: "3px",
          },
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        <SegmentsList segments={get(currentData, "segments", [])} tab={tab} />
      </List>
    </Box>
  );
};
